import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { useTenant } from './TenantContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Create context for preloaded dashboard data
const DashboardDataContext = createContext(null);

export function useDashboardData() {
  return useContext(DashboardDataContext);
}

/**
 * DashboardDataProvider preloads ALL essential data on initial load.
 * The initial spinner covers this — then every page renders instantly.
 */
export function DashboardDataProvider({ children }) {
  const { tenant, getToken } = useTenant();
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  // --- Existing cached data ---
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [walletStatus, setWalletStatus] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [customersSummary, setCustomersSummary] = useState(null);
  const [segmentsSummary, setSegmentsSummary] = useState(null);
  const [settings, setSettings] = useState(null);

  // --- NEW: full first-page data for every tab ---
  const [customersPage, setCustomersPage] = useState(null);
  const [recentUnsubscribes, setRecentUnsubscribes] = useState(null);
  const [campaignsPage, setCampaignsPage] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [smsLogsPage, setSmsLogsPage] = useState(null);
  const [smsStats, setSmsStats] = useState(null);
  const [smsCampaignOptions, setSmsCampaignOptions] = useState(null);
  const [timezones, setTimezones] = useState(null);
  const [team, setTeam] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [emailBalance, setEmailBalance] = useState(null);
  const [googleReviewStats, setGoogleReviewStats] = useState(null);
  const [reviewSubscription, setReviewSubscription] = useState(null);

  const hasLoadedRef = useRef(false);

  const loadDashboardData = useCallback(async () => {
    if (hasLoadedRef.current || !tenant?.has_business) return;
    hasLoadedRef.current = true;

    try {
      const token = await getToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Fire ALL fetches in parallel — spinner covers the wait
      const results = await Promise.allSettled([
        fetch(`${BACKEND_URL}/api/stripe/subscription-status`, { headers }),  // 0
        fetch(`${BACKEND_URL}/api/dashboard/summary`, { headers }),           // 1
        fetch(`${BACKEND_URL}/api/customers?page_size=20`, { headers }),      // 2
        fetch(`${BACKEND_URL}/api/segments`, { headers }),                    // 3
        fetch(`${BACKEND_URL}/api/wallet/transactions?page_size=5`, { headers }), // 4
        fetch(`${BACKEND_URL}/api/settings`, { headers }),                    // 5
        fetch(`${BACKEND_URL}/api/campaigns?page_size=20`, { headers }),      // 6
        fetch(`${BACKEND_URL}/api/templates`, { headers }),                   // 7
        fetch(`${BACKEND_URL}/api/sms-logs?page_size=25`, { headers }),       // 8
        fetch(`${BACKEND_URL}/api/sms-logs/stats`, { headers }),              // 9
        fetch(`${BACKEND_URL}/api/sms-logs/campaigns`, { headers }),          // 10
        fetch(`${BACKEND_URL}/api/settings/timezones`, { headers }),          // 11
        fetch(`${BACKEND_URL}/api/team`, { headers }),                        // 12
        fetch(`${BACKEND_URL}/api/customers/recent-unsubscribes?days=30`, { headers }), // 13
        fetch(`${BACKEND_URL}/api/reviews/google-stats`, { headers }),                  // 14
        fetch(`${BACKEND_URL}/api/reviews/subscription-status`, { headers }),            // 15
        fetch(`${BACKEND_URL}/api/email-logs/stats`, { headers }),                       // 16
        fetch(`${BACKEND_URL}/api/wallet/email-balance`, { headers }),                   // 17
      ]);

      // Helper to safely parse a settled result
      const parse = async (idx) => {
        const r = results[idx];
        if (r.status === 'fulfilled' && r.value.ok) return r.value.json();
        return null;
      };

      // Parse all responses (each can fail independently)
      const [
        walletData, summaryData, customersData, segmentsData,
        activityData, settingsData, campaignsData, templatesData,
        smsLogsData, smsStatsData, smsCampaignsData, timezonesData,
        teamData, recentUnsubsData, googleReviewStatsData, reviewSubData,
        emailStatsData,
        emailBalanceData,
      ] = await Promise.all(
        Array.from({ length: 18 }, (_, i) => parse(i))
      );

      if (walletData) setWalletStatus(walletData);
      if (summaryData) setDashboardSummary(summaryData);

      if (customersData) {
        setCustomersSummary({ total: customersData.total || 0, page_size: customersData.page_size });
        setCustomersPage(customersData);
      }

      if (segmentsData) {
        setSegmentsSummary({ total: segmentsData.length || 0, segments: segmentsData });
      }

      if (activityData) setRecentActivity(activityData.transactions || []);
      if (settingsData) setSettings(settingsData);
      if (campaignsData) setCampaignsPage(campaignsData);
      if (templatesData) setTemplates(templatesData);
      if (smsLogsData) setSmsLogsPage(smsLogsData);
      if (smsStatsData) setSmsStats(smsStatsData);
      if (smsCampaignsData) setSmsCampaignOptions(smsCampaignsData);
      if (timezonesData) setTimezones(timezonesData);
      if (teamData) setTeam(teamData);
      if (recentUnsubsData) setRecentUnsubscribes(recentUnsubsData);
      if (googleReviewStatsData) setGoogleReviewStats(googleReviewStatsData);
      if (reviewSubData) setReviewSubscription(reviewSubData);
      if (emailStatsData) setEmailStats(emailStatsData);
      if (emailBalanceData) setEmailBalance(emailBalanceData);

    } catch (err) {
      console.error('Dashboard data preload error:', err);
    } finally {
      setIsInitialLoadComplete(true);
    }
  }, [tenant, getToken]);

  useEffect(() => {
    if (tenant?.has_business && !hasLoadedRef.current) {
      loadDashboardData();
    }
  }, [tenant, loadDashboardData]);

  // --- Refresh functions (for use after mutations) ---

  const refreshWallet = useCallback(async () => {
    try {
      const token = await getToken();
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const [walletRes, activityRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/stripe/subscription-status`, { headers }),
        fetch(`${BACKEND_URL}/api/wallet/transactions?page_size=5`, { headers }),
      ]);
      if (walletRes.ok) setWalletStatus(await walletRes.json());
      if (activityRes.ok) {
        const data = await activityRes.json();
        setRecentActivity(data.transactions || []);
      }
    } catch (err) { console.error('Wallet refresh error:', err); }
  }, [getToken]);

  const refreshSummary = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/dashboard/summary`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) setDashboardSummary(await res.json());
    } catch (err) { console.error('Summary refresh error:', err); }
  }, [getToken]);

  const refreshCustomers = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/customers?page_size=1`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setCustomersSummary({ total: data.total || 0, page_size: data.page_size });
      }
    } catch (err) { console.error('Customers refresh error:', err); }
  }, [getToken]);

  const refreshSegments = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/segments`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setSegmentsSummary({ total: data.length || 0, segments: data });
      }
    } catch (err) { console.error('Segments refresh error:', err); }
  }, [getToken]);

  const refreshEmailBalance = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/wallet/email-balance`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) setEmailBalance(await res.json());
    } catch (err) { console.error('Email balance refresh error:', err); }
  }, [getToken]);

  const refreshReviewData = useCallback(async () => {
    try {
      const token = await getToken();
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const [statsRes, subRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/reviews/google-stats`, { headers }),
        fetch(`${BACKEND_URL}/api/reviews/subscription-status`, { headers }),
      ]);
      if (statsRes.ok) setGoogleReviewStats(await statsRes.json());
      if (subRes.ok) setReviewSubscription(await subRes.json());
    } catch (err) { console.error('Review data refresh error:', err); }
  }, [getToken]);

  return (
    <DashboardDataContext.Provider value={{
      isInitialLoadComplete,

      // Dashboard overview data
      dashboardSummary,
      walletStatus,
      recentActivity,
      customersSummary,
      segmentsSummary,
      settings,

      // Full first-page data for instant page renders
      customersPage,
      recentUnsubscribes,
      campaignsPage,
      templates,
      smsLogsPage,
      smsStats,
      smsCampaignOptions,
      timezones,
      team,

      // Email data
      emailStats,
      emailBalance,

      // Review data
      googleReviewStats,
      reviewSubscription,

      // Refresh functions
      refreshWallet,
      refreshSummary,
      refreshCustomers,
      refreshSegments,
      refreshEmailBalance,
      refreshReviewData,
    }}>
      {children}
    </DashboardDataContext.Provider>
  );
}
